import { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { kes } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, FileSpreadsheet, FileText, Plus, Trash2 } from "lucide-react";
import SectionNav, { SectionLayout } from "@/components/SectionNav";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function monthStartISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

export default function Accounting() {
  const [dateFrom, setDateFrom] = useState(monthStartISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [tab, setTab] = useState("debtors");

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900">Accounting</h1>
        <p className="text-sm text-muted-foreground">
          Debtors &amp; settlements — accountant only. The profit &amp; loss and costs like rent and salaries are in the
          company accounts (the books), entered there as journals.
        </p>
      </div>

      <Card className="flex flex-wrap items-end gap-4 p-4">
        <div className="space-y-1.5">
          <Label className="text-xs">From</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="acct-date-from" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">To</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="acct-date-to" />
        </div>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <SectionLayout nav={<SectionNav label="Accounting" value={tab} onChange={setTab} groups={[
          { label: "Accounting", items: [{ value: "debtors", label: "Debtors & settlements" }] },
        ]} />}>
        <TabsContent value="debtors" className="mt-0">
          <DebtorsTab dateFrom={dateFrom} dateTo={dateTo} />
        </TabsContent>
        </SectionLayout>
      </Tabs>
    </div>
  );
}

// how a customer payment came to a bank, and the banks it can be paid into (the books match the bank by this name)
const PAY_METHODS = [{ value: "", label: "Cash / other" }, { value: "cheque", label: "Cheque" }, { value: "transfer", label: "Bank transfer / RTGS" }];
const PAY_BANKS = ["ABSA", "KCB"];

const PAYMENT_KINDS = [
  { value: "payment", label: "Customer payment" },
  { value: "eft", label: "EFT to bank (card settlement)" },
  { value: "credit_note", label: "Credit note (Rubis Card)" },
  { value: "adjustment", label: "Adjustment / write-off" },
];

function DebtorsTab({ dateFrom, dateTo }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ account: "", date: todayISO(), kind: "payment", amount: "", charges: "", reference: "", note: "", method: "", bank: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/receivables/accounts", { params: { as_of: dateTo } });
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [dateTo]);

  const loadLedger = useCallback(async (key) => {
    if (!key) {
      setLedger(null);
      return;
    }
    try {
      const { data } = await api.get(`/receivables/ledger/${key}`, { params: { date_from: dateFrom, date_to: dateTo } });
      setLedger(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadLedger(selected); }, [selected, loadLedger]);
  // the statement opens at the foot of the page - bring it into view when one is picked
  const ledgerRef = useRef(null);
  const ledgerKey = ledger?.account?.key;
  useEffect(() => {
    if (ledgerKey && ledgerRef.current) ledgerRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [ledgerKey]);

  const accounts = data?.accounts || [];
  const selectedAcc = accounts.find((a) => a.key === form.account);

  // a card account's usual settlement type: Rubis Card by credit note on the Rubis statement, Card POS by EFT
  const defaultKind = (acc) => {
    if (!acc || acc.kind !== "card") return "payment";
    return acc.name.toLowerCase().includes("rubis") ? "credit_note" : "eft";
  };

  const record = async () => {
    setSaving(true);
    try {
      await api.post("/receivables/payments", { ...form, amount: Number(form.amount || 0), charges: Number(form.charges || 0) });
      toast.success("Recorded");
      setForm({ ...form, amount: "", charges: "", reference: "", note: "" });
      await load();
      if (selected === form.account) await loadLedger(selected);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const removePayment = async (id) => {
    try {
      await api.delete(`/receivables/payments/${id}`);
      toast.success("Deleted");
      await load();
      await loadLedger(selected);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // the statement as a PDF on the company letterhead (date, description, debit, credit, balance)
  const downloadPdf = async (key, name) => {
    try {
      const res = await api.get(`/receivables/ledger/${key}/pdf`, { params: { date_from: dateFrom, date_to: dateTo }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Statement - ${name || "account"} ${dateFrom} to ${dateTo}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Statement PDF failed");
    }
  };

  const downloadStatement = async () => {
    try {
      const res = await api.get(`/receivables/ledger/${selected}/excel`, { params: { date_from: dateFrom, date_to: dateTo }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Statement - ${ledger?.account?.name || "account"} ${dateFrom} to ${dateTo}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Statement download failed");
    }
  };

  const typeLabel = { customer: "Customer", card: "Card", attendant: "Attendant (Unpaid)", holding: "To itemize" };

  return (
    <div className="space-y-4">
      <Card className="overflow-hidden">
        <div className="flex items-center justify-between border-b bg-slate-50 px-4 py-3">
          <h3 className="font-display font-semibold text-slate-800">Balances as at {data?.as_of || dateTo}</h3>
          <span className="font-mono-fin font-semibold">Total owed: KSh {kes(data?.total_balance || 0)}</span>
        </div>
        {loading && !data ? <div className="p-6 text-center"><Loader2 className="inline h-5 w-5 animate-spin" /></div> : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Account</TableHead><TableHead>Type</TableHead>
                <TableHead className="text-right">Balance</TableHead><TableHead className="text-right">0–30 days</TableHead>
                <TableHead className="text-right">31–60</TableHead><TableHead className="text-right">61–90</TableHead>
                <TableHead className="text-right">90+</TableHead><TableHead>Last activity</TableHead><TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {accounts.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground">No accounts yet.</TableCell></TableRow>}
              {accounts.map((a) => (
                <TableRow key={a.key} className={selected === a.key ? "bg-slate-50" : ""} data-testid={`debtor-row-${a.key}`}>
                  <TableCell className="font-medium">
                    {a.name}
                    {a.over_limit && <span className="ml-2 rounded bg-rose-100 px-1.5 py-0.5 text-[10px] text-rose-700">over limit</span>}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{typeLabel[a.kind] || a.kind}</TableCell>
                  <TableCell className={`text-right font-mono-fin font-semibold ${a.balance > 0 ? "text-slate-900" : "text-emerald-700"}`}>{kes(a.balance)}</TableCell>
                  <TableCell className="text-right font-mono-fin">{kes(a.ageing.current)}</TableCell>
                  <TableCell className="text-right font-mono-fin">{kes(a.ageing.d31_60)}</TableCell>
                  <TableCell className="text-right font-mono-fin">{kes(a.ageing.d61_90)}</TableCell>
                  <TableCell className={`text-right font-mono-fin ${a.ageing.d90_plus > 0 ? "text-rose-600" : ""}`}>{kes(a.ageing.d90_plus)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{a.last_activity || "—"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1.5">
                      <Button variant="ghost" size="sm" data-testid={`debtor-open-${a.key}`}
                        onClick={() => (selected === a.key ? ledgerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }) : setSelected(a.key))}>View</Button>
                      <Button variant="outline" size="sm" data-testid={`debtor-pdf-${a.key}`} onClick={() => downloadPdf(a.key, a.name)}>
                        <FileText className="mr-1.5 h-4 w-4" />Statement
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Card className="p-5">
        <h3 className="mb-3 font-display font-semibold text-slate-800">Record payment / settlement</h3>
        <div className="grid gap-3 md:grid-cols-4">
          <div className="space-y-1.5 md:col-span-2">
            <Label className="text-xs">Account</Label>
            <Select value={form.account} onValueChange={(v) => setForm({ ...form, account: v, kind: defaultKind(accounts.find((a) => a.key === v)) })}>
              <SelectTrigger data-testid="pay-account"><SelectValue placeholder="Select account" /></SelectTrigger>
              <SelectContent>
                {accounts.filter((a) => a.kind !== "holding").map((a) => <SelectItem key={a.key} value={a.key}>{a.name} — owes {kes(a.balance)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Type</Label>
            <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v, ...(v === "payment" ? {} : { method: "", bank: "" }) })}>
              <SelectTrigger data-testid="pay-kind"><SelectValue /></SelectTrigger>
              <SelectContent>{PAYMENT_KINDS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {form.kind === "payment" && (
            <div className="space-y-1.5">
              <Label className="text-xs">How paid</Label>
              <Select value={form.method || "none"} onValueChange={(v) => setForm({ ...form, method: v === "none" ? "" : v, bank: v === "none" ? "" : form.bank })}>
                <SelectTrigger data-testid="pay-method"><SelectValue /></SelectTrigger>
                <SelectContent>{PAY_METHODS.map((m) => <SelectItem key={m.value || "none"} value={m.value || "none"}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {form.kind === "payment" && form.method && (
            <div className="space-y-1.5">
              <Label className="text-xs">Paid into bank</Label>
              <Select value={form.bank} onValueChange={(v) => setForm({ ...form, bank: v })}>
                <SelectTrigger data-testid="pay-bank"><SelectValue placeholder="Select bank" /></SelectTrigger>
                <SelectContent>{PAY_BANKS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs">Date received</Label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="pay-date" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Amount received (KES)</Label>
            <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="pay-amount" />
          </div>
          {selectedAcc?.kind === "card" && (
            <div className="space-y-1.5">
              <Label className="text-xs">Charges / commission deducted (KES)</Label>
              <Input type="number" value={form.charges} onChange={(e) => setForm({ ...form, charges: e.target.value })} data-testid="pay-charges" />
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs">Reference (receipt, EFT ref, credit note no.)</Label>
            <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="pay-reference" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Note</Label>
            <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="pay-note" />
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <Button onClick={record} disabled={saving || !form.account || !(Number(form.amount) || Number(form.charges)) || (form.method && !form.bank)}
            className="bg-brand hover:bg-brand/90" data-testid="pay-save">
            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Plus className="mr-1.5 h-4 w-4" />}Record
          </Button>
          {selectedAcc && (
            <span className="text-xs text-muted-foreground">
              Clears KSh {kes((Number(form.amount) || 0) + (Number(form.charges) || 0))} of {selectedAcc.name}&apos;s KSh {kes(selectedAcc.balance)}.
            </span>
          )}
        </div>
      </Card>

      {ledger && (
        <Card className="overflow-hidden scroll-mt-20" ref={ledgerRef} data-testid="statement-card">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-slate-50 px-4 py-3">
            <h3 className="font-display font-semibold text-slate-800">
              Statement — {ledger.account.name} <span className="text-xs font-normal text-muted-foreground">({dateFrom} to {dateTo})</span>
            </h3>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => downloadPdf(selected, ledger.account.name)} data-testid="statement-pdf"><FileText className="mr-1.5 h-4 w-4" />PDF</Button>
              <Button variant="outline" size="sm" onClick={downloadStatement} data-testid="statement-excel"><FileSpreadsheet className="mr-1.5 h-4 w-4" />Excel</Button>
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead><TableHead>Shift</TableHead><TableHead>Type</TableHead><TableHead>Details</TableHead>
                <TableHead className="text-right">Charged</TableHead><TableHead className="text-right">Paid / settled</TableHead>
                <TableHead className="text-right">Balance</TableHead><TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell colSpan={6} className="text-muted-foreground">Opening balance</TableCell>
                <TableCell className="text-right font-mono-fin">{kes(ledger.opening)}</TableCell><TableCell />
              </TableRow>
              {ledger.entries.map((e, i) => (
                <TableRow key={i}>
                  <TableCell>{e.date}</TableCell>
                  <TableCell className="text-xs">{e.shift}{e.status && e.status !== "locked" ? <span className="ml-1 text-muted-foreground">({e.status})</span> : null}</TableCell>
                  <TableCell className="text-xs">{e.type === "charge" ? "Charge" : PAYMENT_KINDS.find((k) => k.value === e.type)?.label || e.type}</TableCell>
                  <TableCell className="text-xs">{e.description}{e.attendant ? <span className="text-muted-foreground"> — {e.attendant}</span> : null}</TableCell>
                  <TableCell className="text-right font-mono-fin">{e.debit ? kes(e.debit) : ""}</TableCell>
                  <TableCell className="text-right font-mono-fin text-emerald-700">{e.credit ? kes(e.credit) : ""}</TableCell>
                  <TableCell className="text-right font-mono-fin font-semibold">{kes(e.balance)}</TableCell>
                  <TableCell className="text-right">
                    {e.id && <Button variant="ghost" size="icon" onClick={() => removePayment(e.id)} title="Delete this entry"><Trash2 className="h-4 w-4 text-rose-500" /></Button>}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow className="font-semibold">
                <TableCell colSpan={6}>Closing balance</TableCell>
                <TableCell className="text-right font-mono-fin">{kes(ledger.closing)}</TableCell><TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
